import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';

import { AppFeatures } from 'src/app/core/app.features';
import { AppState } from 'src/app/reducers';
import { FormsService } from 'src/app/core/services/forms.service';
import { GeneralDynamicFormComponent } from '../../general-dynamic-form/general-dynamic-form.component';
import { LoggerService } from '@services/logger.service';
import { NomObjectTypes } from 'src/app/core/constants';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { injectAvailableFeedTypesQuery } from 'src/app/api/queries/user.queries';
import { selectUser } from 'src/app/core/reducers';

@Component({
  selector: 'app-feed-type-selector',
  templateUrl: './feed-type-selector.component.html',
  styleUrls: ['./feed-type-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FeedTypeSelectorComponent
  extends GeneralDynamicFormComponent
  implements OnInit
{
  @Input() selectedFeedTypeId: number = NomObjectTypes.ONLINE_FEED;
  @Output() feedTypeIdChange = new EventEmitter<number>();
  @Output() componentDataChange: EventEmitter<any> = new EventEmitter();

  private user = this.store.selectSignal(selectUser);

  private userId = computed(() => this.user()?.id ?? '');

  availableFeedTypesQuery = injectAvailableFeedTypesQuery(this.userId());

  feedTypes = computed(() => {
    const feedTypesData = this.availableFeedTypesQuery.data() ?? [];
    const user = this.user();

    const baseTypes = [
      {
        id: NomObjectTypes.ONLINE_FEED,
        name: this.translateService.instant('feed-type-selector.name.online'),
        description: this.translateService.instant(
          'feed-type-selector.description.online',
        ),
        image: 'assets/img/feeds/online.png',
        left: null as number | null,
      },
      {
        id: NomObjectTypes.SOCIAL_FEED,
        name: this.translateService.instant('feed-type-selector.name.social'),
        description: this.translateService.instant(
          'feed-type-selector.description.social',
        ),
        image: 'assets/img/feeds/social.png',
        left: null as number | null,
      },
      {
        id: NomObjectTypes.BROADCAST_FEED,
        name: this.translateService.instant('feed-type-selector.name.premium'),
        description: this.translateService.instant(
          'feed-type-selector.description.premium',
        ),
        image: 'assets/img/feeds/premium.png',
        left: null as number | null,
      },
      {
        id: NomObjectTypes.COMBINED_FEED,
        name: this.translateService.instant('feed-type-selector.name.combined'),
        description: this.translateService.instant(
          'feed-type-selector.description.combined',
        ),
        image: 'assets/img/feeds/combined.png',
        left: null as number | null,
      },
      {
        id: NomObjectTypes.CLIPPING_FEED,
        name: this.translateService.instant('feed-type-selector.name.clipping'),
        description: this.translateService.instant(
          'feed-type-selector.description.clipping',
        ),
        image: 'assets/img/feeds/clipping.png',
        left: null as number | null,
      },
      {
        id: NomObjectTypes.STORY_FEED,
        name: this.translateService.instant('feed-type-selector.name.story'),
        description: this.translateService.instant(
          'feed-type-selector.description.story',
        ),
        image: 'assets/img/feeds/story.png',
        left: null as number | null,
      },
    ];

    const hasStoriesAccess =
      user?.feature_components?.[AppFeatures.STORIES_ACCESS];

    const filteredTypes = hasStoriesAccess
      ? baseTypes
      : baseTypes.filter((ft) => ft.id !== NomObjectTypes.STORY_FEED);

    return filteredTypes.map((obj) => {
      const match = feedTypesData.find((obj2) => obj2.type_id === obj.id);
      return {
        ...obj,
        left: match?.left ?? 0,
      };
    });
  });

  constructor(
    protected formService: FormsService,
    protected store: Store<AppState>,
    protected translateService: TranslateService,
    protected logger: LoggerService,
  ) {
    super(formService, store, logger);
  }

  ngOnInit(): void {
    super.ngOnInit();
  }

  selectFeedTypeId(feedType: number): void {
    this.selectedFeedTypeId = feedType;
    this.feedTypeIdChange.emit(feedType);
    this.componentDataChange.emit(feedType);
  }
}
